import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, Loader2, Clock, MessageCircle } from "lucide-react";
import api from "../api";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import { useContent } from "../hooks/useContent";

const ContactUs = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useSeo({ title: t("ct.contactUs"), description: t("ct.questionsPartnershipsOrFeedback") });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast({ title: t("ct.messageTooShort") });
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast({ title: t("ct.messageSent"), description: t("ct.thanksForReachingOut") });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("ct.messageFailed"), description: typeof d === "string" ? d : t("ct.tryAgainLater") });
    } finally { setSending(false); }
  };

  const cmsRaw = useContent("contact");
  const cms = cmsRaw || {};
  const cmsReady = cmsRaw !== null;   // no flash of default phone/address before the admin's settings land

  const info = [
    { key: "email", show: cms.showEmail !== false, icon: Mail, label: "Email", value: cms.email || "support@lytepress.com" },
    { key: "phone", show: cms.showPhone !== false, icon: Phone, label: "Phone", value: cms.phone || "+1 (868) 555-0123" },
    { key: "location", show: cms.showLocation !== false, icon: MapPin, label: "Location", value: cms.location || "Port of Spain, Trinidad & Tobago" },
    { key: "hours", show: cms.showHours !== false && !!cms.hours, icon: Clock, label: "Opening hours", value: cms.hours },
  ].filter((it) => it.show);
  const waNumber = String(cms.whatsapp || "").replace(/[^0-9]/g, "");
  const showWa = cms.showWhatsapp !== false && !!waNumber;
  const showInfo = cmsReady && !cms.hideInfo && (info.length > 0 || showWa);

  return (
    <div className="space-y-6 lp-fade-up">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground">{t("ct.contactUs")}</h1>
        <p className="text-sm text-muted-foreground">{t("ct.questionsPartnershipsOrFeedback")}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {!cmsReady && (
          <div className="lg:col-span-2 space-y-4" data-testid="contact-info-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[92px] rounded-2xl border border-border bg-secondary/60 animate-pulse" />
            ))}
          </div>
        )}
        {showInfo && (
        <div className="lg:col-span-2 space-y-4" data-testid="contact-info-column">
          {info.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.label} className="flex items-start gap-4 bg-card border border-border rounded-2xl p-5"
                data-testid={`contact-info-${it.key}`}>
                <span className="h-11 w-11 rounded-xl bg-[#e7f6ec] flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-[#16a34a]" />
                </span>
                <div>
                  <p className="text-sm text-muted-foreground">{it.label}</p>
                  <p className="font-semibold text-foreground whitespace-pre-line">{it.value}</p>
                </div>
              </div>
            );
          })}
          {showWa && (
            <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
              className="flex items-center gap-3 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold rounded-2xl p-5 transition-colors">
              <span className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <MessageCircle size={20} />
              </span>
              <span>
                <span className="block text-sm text-white/80 font-normal">WhatsApp</span>
                Chat with us
              </span>
            </a>
          )}
        </div>
        )}

        <form onSubmit={submit} className={`${showInfo || !cmsReady ? "lg:col-span-3" : "lg:col-span-5"} bg-card border border-border rounded-2xl p-6 space-y-4`} data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={update("name")} placeholder={t("ct.yourName")} data-testid="contact-name" className="border border-border rounded-xl px-4 py-3 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground" />
            <input required type="email" value={form.email} onChange={update("email")} placeholder={t("ct.emailAddress")} data-testid="contact-email" className="border border-border rounded-xl px-4 py-3 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground" />
          </div>
          <input value={form.subject} onChange={update("subject")} placeholder={t("ct.subject")} data-testid="contact-subject" className="w-full border border-border rounded-xl px-4 py-3 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground" />
          <textarea required value={form.message} onChange={update("message")} placeholder={t("ct.yourMessage")} rows={6} data-testid="contact-message" className="w-full border border-border rounded-xl px-4 py-3 text-sm bg-transparent outline-none focus:border-[#16a34a] resize-none text-foreground" />
          <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-xl transition-colors">
            {sending ? <><Loader2 size={16} className="animate-spin" /> {t("ct.sending")}</> : <>{t("ct.sendMessage")} <Send size={16} /></>}
          </button>
          <p className="text-xs text-muted-foreground">{t("ct.replyNote")}</p>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;
